import { createContext, useContext, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { getCreator, getMe, getPublicCreator } from "../data/client";
import type { Creator, Game, Me, Tier } from "../data/types";
import { warmSheets } from "../design/sprites";

/**
 * D-1's seam.
 *
 * Single-creator today, multi-tenant by construction. **No component, service
 * or query reads a creator constant directly** - they read this context. When
 * multi-tenancy arrives, `resolveCreatorId` starts reading a hostname,
 * subdomain or route param and nothing else moves.
 */

type CreatorState = {
  creator: Creator;
  /**
   * null for a signed-out visitor. Only the landing renders in that state, and
   * it does not read `me` - but the type says so rather than lying with a
   * non-null assertion.
   */
  me: Me | null;
  /** Convenience lookups, so screens never scan the arrays themselves. */
  gameById: (id: string) => Game | undefined;
  tierById: (id: string) => Tier | undefined;
  /** Games still offered in pickers. A retired game stays resolvable (D-12). */
  activeGames: Game[];
  franchises: string[];
};

const Context = createContext<CreatorState | null>(null);

/**
 * Which creator this request is for. Today: the only one. Later: hostname,
 * subdomain or route param. This function is the whole of the change.
 */
export function resolveCreatorId(): string | undefined {
  return undefined;
}

type Status =
  | { phase: "loading" }
  | { phase: "error"; error: Error }
  | { phase: "ready"; creator: Creator; me: Me | null };

export function CreatorProvider({
  children,
  fallback,
  onError,
  signedIn,
}: {
  children: ReactNode;
  fallback: ReactNode;
  onError: (error: Error) => ReactNode;
  /**
   * Chooses the source. Signed out reads `/public/creator`, the one route
   * outside the authorizer; signed in reads the full profile and the session.
   */
  signedIn: boolean;
}) {
  const [status, setStatus] = useState<Status>({ phase: "loading" });

  useEffect(() => {
    let live = true;
    const load: Promise<[Creator, Me | null]> = signedIn
      ? Promise.all([getCreator(), getMe()])
      : getPublicCreator().then((creator) => [creator, null]);

    load
      .then(([creator, me]) => {
        if (!live) return;
        // FR-14 asks for sprites before first paint. A game's tint is creator
        // data, so the earliest possible moment is here - the instant the
        // record lands, and before any card renders.
        warmSheets(creator.games.map((g) => g.tint));
        setStatus({ phase: "ready", creator, me });
      })
      .catch((error: Error) => live && setStatus({ phase: "error", error }));
    return () => {
      live = false;
    };
  }, [signedIn]);

  const value = useMemo<CreatorState | null>(() => {
    if (status.phase !== "ready") return null;
    const { creator, me } = status;
    return {
      creator,
      me,
      gameById: (id) => creator.games.find((g) => g.id === id),
      tierById: (id) => creator.tiers.find((t) => t.id === id),
      activeGames: creator.games.filter((g) => !g.retired),
      franchises: [
        ...new Set(creator.games.filter((g) => !g.retired).map((g) => g.franchise)),
      ].sort(),
    };
  }, [status]);

  if (status.phase === "loading") return <>{fallback}</>;
  if (status.phase === "error") return <>{onError(status.error)}</>;
  return <Context.Provider value={value}>{children}</Context.Provider>;
}

export function useCreator(): CreatorState {
  const value = useContext(Context);
  if (!value) {
    throw new Error("useCreator must be used inside <CreatorProvider>");
  }
  return value;
}
